import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useReadLocalStorage } from 'usehooks-ts';

import { logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';
import { isLikelyMobile } from '~/utils/isLikelyMobile';
import { haptic } from '~/utils/haptic';
import { onboardingSfx } from '~/utils/onboardingSfx';

import { sendOnboardingLink } from './sendOnboardingLink';
import styles from './OnboardingBridge.module.css';

/**
 * Passerelle d'onboarding MOBILE : le tuto jouable est impossible sur
 * téléphone (il faudrait scanner son propre écran), alors on retourne la
 * frustration en promesse — « ta manette, tu l'as déjà en main » — et on
 * envoie le lien pour continuer sur un ordinateur, où le tuto se lancera
 * tout seul (flag onboardingCompleted).
 *
 * Feedback VISUAL-FIRST : pas d'API de vibration sur iOS Safari, et le Web
 * Audio est coupé par le mode silencieux iPhone — son et haptique sont des
 * bonus, jamais le message.
 */

type Phase = 'press' | 'pressed' | 'sent';

const CONFETTI_COLORS = ['#ff2453', '#20abf3', '#1bc65f', '#f3ca20', '#8b5cf6'];
const CONFETTI_COUNT = 26;

/* Pseudo-aléatoire déterministe par index — largement assez pour des confettis. */
function rand(i: number, salt: number) {
    return Math.abs(Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453) % 1;
}

function confettiStyle(i: number): CSSProperties {
    const angle = (i / CONFETTI_COUNT) * 2 * Math.PI + rand(i, 1) * 0.5;
    const dist = 90 + rand(i, 2) * 140;
    return {
        '--dx': `${Math.cos(angle) * dist}px`,
        '--dy': `${Math.sin(angle) * dist - 70}px`,
        '--rot': `${(rand(i, 3) - 0.5) * 720}deg`,
        '--d': `${rand(i, 4) * 0.12}s`,
        '--clr': CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    } as CSSProperties;
}

export function OnboardingBridge() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const clientID = useReadLocalStorage<string>('clientID') || '';

    const [phase, setPhase] = useState<Phase>('press');
    const [email, setEmail] = useState<string>(() => auth?.email || '');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(false);

    const confetti = useMemo(() => Array.from({ length: CONFETTI_COUNT }, (_, i) => confettiStyle(i)), []);

    // Page pensée pour téléphone : sur ordinateur, le tuto se lance
    // directement depuis la home — on n'a rien à faire ici. ?preview=1
    // permet de la tester depuis un navigateur desktop.
    const [searchParams] = useSearchParams();
    const previewMode = searchParams.get('preview') === '1';
    const redirectedRef = useRef(false);
    useEffect(() => {
        if (!previewMode && !isLikelyMobile()) {
            redirectedRef.current = true;
            navigate('/home', { replace: true });
        }
    }, []);

    useEffect(() => {
        if (redirectedRef.current) return;
        document.title = 'Unboared';
        logEvent('onboarding_bridge_shown', clientID);
    }, []);

    const handlePress = () => {
        if (phase !== 'press') return;
        haptic([40, 60, 40]);
        onboardingSfx.press();
        logEvent('onboarding_bridge_cta_tapped', clientID);
        setPhase('pressed');
    };

    const handleSend = async () => {
        if (sending) return;
        setSending(true);
        setSendError(false);
        try {
            await sendOnboardingLink(email, i18n.language);
            logEvent('onboarding_bridge_email_sent', clientID);
            onboardingSfx.sent();
            haptic(30);
            setPhase('sent');
        } catch {
            logEvent('onboarding_bridge_email_error', clientID);
            setSendError(true);
        } finally {
            setSending(false);
        }
    };

    const handleSkip = () => {
        logEvent('onboarding_bridge_skipped', clientID);
        navigate('/home', { replace: true });
    };

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return (
        <div className={styles.page}>
            <img src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" className={styles.logo} />

            <main className={styles.content}>
                {phase === 'press' && (
                    <header className={styles.header}>
                        <p className={styles.eyebrow}>{t('onboarding.bridge.eyebrow')}</p>
                        <h1 className={styles.title}>{t('onboarding.bridge.title')}</h1>
                        <p className={styles.subtitle}>{t('onboarding.bridge.subtitle')}</p>
                    </header>
                )}

                {phase === 'pressed' && (
                    <header className={styles.header}>
                        <h1 className={styles.title}>{t('onboarding.bridge.pressedTitle')}</h1>
                        <p className={styles.subtitle}>{t('onboarding.bridge.pressedSubtitle')}</p>
                    </header>
                )}

                {phase !== 'sent' && (
                    <div className={styles.buttonZone}>
                        <button
                            type="button"
                            className={`${styles.arcade} ${phase === 'pressed' ? styles.arcadePressed : ''}`}
                            onClick={handlePress}
                        >
                            {phase === 'press' ? t('onboarding.bridge.press') : '🎮'}
                        </button>
                        {phase === 'pressed' && <span className={styles.shockwave} />}
                        {phase === 'pressed' &&
                            confetti.map((style, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <span key={i} className={styles.confetti} style={style} />
                            ))}
                    </div>
                )}

                {phase === 'pressed' && (
                    <div className={styles.emailCard}>
                        <p className={styles.emailLabel}>{t('onboarding.bridge.emailLabel')}</p>
                        <input
                            className={styles.emailInput}
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('onboarding.bridge.emailPlaceholder')}
                        />
                        <button
                            type="button"
                            className={styles.send}
                            onClick={handleSend}
                            disabled={!emailValid || sending}
                        >
                            {sending ? t('onboarding.bridge.sending') : t('onboarding.bridge.send')}
                        </button>
                        {sendError && <p className={styles.sendError}>{t('onboarding.bridge.sendError')}</p>}
                    </div>
                )}

                {phase === 'sent' && (
                    <div className={styles.sentBlock}>
                        <div className={styles.mailGlyph}>📬</div>
                        <h1 className={styles.title}>{t('onboarding.bridge.sentTitle')}</h1>
                        <p className={styles.subtitle}>{t('onboarding.bridge.sentSubtitle')}</p>
                        <p className={styles.sentEmail}>{email}</p>
                        <p className={styles.spamHint}>{t('onboarding.bridge.sentSpamHint')}</p>
                        <button type="button" className={styles.resend} onClick={handleSend} disabled={sending}>
                            {sending ? t('onboarding.bridge.sending') : t('onboarding.bridge.resend')}
                        </button>
                        {sendError && <p className={styles.sendError}>{t('onboarding.bridge.sendError')}</p>}
                    </div>
                )}
            </main>

            <footer className={styles.footer}>
                <button type="button" className={styles.skip} onClick={handleSkip}>
                    {phase === 'sent' ? t('onboarding.bridge.continueMobile') : t('onboarding.bridge.later')}
                </button>
            </footer>
        </div>
    );
}
